import { getTelegramInitData, isTelegram } from "../telegram";

export type ScoreEntry = {
  id: number;
  name: string;
  score: number;
  created_at: string;
};

const NAME_KEY = "exorunner_name";

export function getPlayerName(): string {
  return localStorage.getItem(NAME_KEY)?.trim() || "";
}

export function setPlayerName(name: string): void {
  localStorage.setItem(NAME_KEY, name.trim());
}

export async function fetchLeaderboard(limit = 20): Promise<ScoreEntry[]> {
  const res = await fetch(`/api/leaderboard?limit=${limit}`);
  if (!res.ok) throw new Error("Could not load leaderboard");
  const data = (await res.json()) as { scores: ScoreEntry[] };
  return data.scores ?? [];
}

let currentSessionId = "";
let currentSessionToken = "";
let latestReceipt = "";
let sessionReady: Promise<void> | null = null;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let getScoreFn: (() => number) | null = null;
let submitted = false;

export function startSession(scoreFn: () => number): void {
  currentSessionId = crypto.randomUUID?.() ??
    (Math.random().toString(36).slice(2) + Date.now().toString(36));
  currentSessionToken = "";
  latestReceipt = "";
  getScoreFn = scoreFn;
  submitted = false;

  sessionReady = (async () => {
    try {
      const res = await fetch("/api/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: currentSessionId }),
      });
      if (res.ok) {
        const data = (await res.json()) as { sessionToken: string };
        currentSessionToken = data.sessionToken;
        // Send first heartbeat immediately
        sendHeartbeat();
      }
    } catch {
      currentSessionToken = "";
    }
  })();

  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    sendHeartbeat();
  }, 3000);
}

export function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

async function sendHeartbeat(): Promise<void> {
  if (!currentSessionId || !currentSessionToken || !getScoreFn) return;
  const score = getScoreFn();
  try {
    const res = await fetch("/api/heartbeat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: currentSessionId,
        sessionToken: currentSessionToken,
        score,
      }),
    });
    if (res.ok) {
      const data = (await res.json()) as { ok: boolean; receipt?: string };
      if (data.receipt) {
        latestReceipt = data.receipt;
      }
    }
  } catch {}
}

export async function submitLeaderboardScore(
  name: string,
  score: number
): Promise<{ rank: number; entry: ScoreEntry }> {
  if (submitted) throw new Error("Already submitted");
  submitted = true;

  stopHeartbeat();

  // Wait for session token
  if (sessionReady) await sessionReady;

  // Send final heartbeat with actual final score and get receipt
  if (currentSessionId && currentSessionToken) {
    await sendHeartbeat();
    // Also send one with the exact final score
    try {
      const res = await fetch("/api/heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: currentSessionId,
          sessionToken: currentSessionToken,
          score,
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as { ok: boolean; receipt?: string };
        if (data.receipt) {
          latestReceipt = data.receipt;
        }
      }
    } catch {}
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (isTelegram) {
    const initData = getTelegramInitData();
    if (initData) {
      headers["X-Telegram-Init-Data"] = initData;
    }
  }

  const res = await fetch("/api/leaderboard", {
    method: "POST",
    headers,
    body: JSON.stringify({
      name,
      score,
      sessionId: currentSessionId,
      sessionToken: currentSessionToken,
      receipt: latestReceipt,
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.error ?? "Submit failed");
  }
  return data as { rank: number; entry: ScoreEntry };
}
